import S from 's-js';
import { SArray as ArraySignal, lift } from 's-array'
import { IAjax } from 'lib/Ajax';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { Mutex } from 'lib/mutex';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';
import { tzmomentMod } from 'lib/tzmoment';
import uniqueIndex from 'lib/uniqueIndex';
import { ValidationWatcher, Errors } from 'lib/validationWatcher';

import { BerthReservation } from 'data/models/BerthReservation';
import { BookReservationsRequestData } from 'data/models/BookReservationsRequest';
import { CoordinationCard } from 'data/models/CoordinationCard';
import { CoordinationCardVersion } from 'data/models/CoordinationCardVersion';
import { Issue } from 'data/models/Issue';
import { MilestoneTypes as MSType } from 'data/models/MilestoneTypes';
import { PortCall } from 'data/models/PortCall';
import { PortCallChange } from 'data/models/PortCallChange';
import { PortCallRights } from 'server/enums/Rights';
import { PortCallScheduleResponse, PortCallScheduleResponseOptions } from 'data/models/PortCallScheduleResponse';
import { ScheduleDateEntity as DateEntity } from 'data/models/ScheduleDateEntity';
import { ScheduleDateEvent as DateEvent } from 'data/models/ScheduleDateEvent';
import { ScheduleDateEventType as SDEType } from 'data/models/ScheduleDateEventType';
import { UnbookReservationsRequestData } from 'data/models/UnbookReservationsRequest';

import { PCCache } from 'data/PCCache';

import { LocalizationContext } from 'common/models/LocalizationContext';

import {
    ScheduleChangeEngine,
    ScheduleChangeEngineParams,
    Policy,
    eventKey,
    buildEventKey,
    ScheduleRule as Rule,
    ScheduleRel as Rel
} from 'common/services/scheduleChangeEngine';
import { commonScheduleRules } from 'common/services/commonScheduleRules';

import { Entrypoints } from 'core/app/entrypoints';

import { BerthAssignmentDetails, BerthAssignmentDetailsParams } from 'portcall/berthassignmentdetails/model';
import {
    CancelledCoordinationCardDetails,
    CancelledCoordinationCardDetailsParams
} from 'portcall/coordinationcarddetails/cancelled';
import { CoordinationCardDetails, CoordinationCardDetailsParams } from 'portcall/coordinationcarddetails/model';
import {
    MetroMapFramework,
    MetroMapFrameworkParams,
    MetroMapMilestone,
    MetroMapMilestoneParams,
    MilestoneParams
} from 'portcall/metromapframework/model';

import { PortCallScheduleHeader, PortCallScheduleHeaderParams } from './header';

// Metro map TODOs...
//  - Support change berth

export interface CoordinationCardController {
    ShouldPauseLogUpdates? : () => boolean;
}

export interface CancelledCoordinationCardController {
    ShouldPauseLogUpdates? : () => boolean;
}

export interface PortCallScheduleParams {
    PortCall : PortCall;
    ccardController? : (ccard : CoordinationCard) => CoordinationCardController;
    cancelledCCardController? : (ccv : CoordinationCardVersion) => CancelledCoordinationCardController;
}

export type PortCallSchedule = ReturnType<typeof PortCallSchedule>;
export type PortCallMilestone = ReturnType<PortCallSchedule["Framework"]["Milestones"]>[0];
export function PortCallSchedule(
    loader : CachedModelLoader<PortCallScheduleResponse, PortCallScheduleResponseOptions>,
    data : PCCache,
    ajax : IAjax,
    entrypoints : Entrypoints,
    tzutils : TZUtils,
    Localization : LocalizationContext,
    scheduleChangeEngineModel : (params : ScheduleChangeEngineParams) => ScheduleChangeEngine,
    metroMapFrameworkModel : <TMilestone>(params : MetroMapFrameworkParams<TMilestone>) => MetroMapFramework<TMilestone>,
    metroMapMilestoneModel : (params : MetroMapMilestoneParams) => MetroMapMilestone,
    berthAssgnDetailsModel : (params : BerthAssignmentDetailsParams) => BerthAssignmentDetails,
    ccardDetailsModel : (params : CoordinationCardDetailsParams) => CoordinationCardDetails,
    cancelledCCardDetailsModel : (params : CancelledCoordinationCardDetailsParams) => CancelledCoordinationCardDetails,
    headerModel : (params : PortCallScheduleHeaderParams) => PortCallScheduleHeader,
    {
        PortCall,
        ccardController,
        cancelledCCardController
    } : PortCallScheduleParams
) {
    const
        { model, loading: initialDataLoading } = loader({ Id: PortCall.Id() }),
        SetEditState = S.value<EditStateParams | null>(null),
        CurrentEditState = S(() => SetEditState() && EditState(SetEditState()!, () => SetEditState(null))),
        CurrentEditPhase = () => CurrentEditState()?.Phase() ?? null,
        scheduleEngine = S.on(CurrentEditState, () => {
            const 
                edit = CurrentEditState(),
                Policy = edit?.SchedulePolicy;

            if (!edit || !Policy) return null;

            const
                date = edit.Date,
                logActual = edit.Type === EditType.LogActual,
                clearActual = edit.Type === EditType.ClearActual,
                editActual = edit.Type === EditType.EditActual,
                isActual = logActual || clearActual || editActual,
                engine = scheduleChangeEngineModel({ PortCall, Policy }),
                enter = new DateEvent({ Id: 0, 
                    Type: isActual ? SDEType.ActualDateEntered : SDEType.EstimatedDateEntered,
                    ScheduleDateId: date.Id(),
                    // For logging actual, default to current date/time, but rounded down to the nearest minute
                    // since otherwise you can wind up with small rounding artifacts in our UI since our UI
                    // only displays dates to the nearest minute and our schedule engine only compares dates
                    // to the nearest minute.
                    Date: logActual ? tzmomentMod(tzutils, tzutils.now(), m => m.startOf('minute')).toJSON() : 
                        clearActual ? null :
                        date.Effective().toJSON()
                });

                enter.ScheduleDate(date);
                enter.ScheduleChange(engine.ScheduleChange);
                engine.ScheduleChange.ScheduleEvents.push(enter);

            return engine;
        }),
        scheduleChange = () => scheduleEngine() && scheduleEngine()!.ScheduleChange,
        scheduleEvents = lift(() => scheduleChange() && scheduleChange()!.ScheduleEvents() || []),
        eventIndex = uniqueIndex(scheduleEvents, eventKey);

    const
        pcChangesClient = ajax.Url<PortCallChange>(() => PortCall.ChangesUrl() ?? '', { useCache : true, include: [ c => c.ScheduleEvents ] }),
        pcChangesCommitHandler : CommitHandler = {
            async Commit() { scheduleChange() && await pcChangesClient.Post(scheduleChange()!); },
            NewValidationWatcher: () => ValidationWatcher(pcChangesClient)
        };

    const
        Framework = metroMapFrameworkModel({
            PortCall,
            MilestoneConstructor: Milestone
        }),
        milestones = Framework.Milestones,
        CanEditPortCall = () => (model.Rights() & PortCallRights.Edit       ) !== 0,
        CanEditActuals  = () => (model.Rights() & PortCallRights.EditActuals) !== 0,
        CanMovePortCall = () => CanEditPortCall() && CurrentEditState() === null && milestones().length > 0 && PortCall.Schedule().every(d => d.Actual() === null),
        MovePortCall = () => SetEditState(MovePortCallState(milestones()[0].Date, pcChangesCommitHandler)),
        focusedMilestone : () => ReturnType<typeof Milestone> | null = () => CurrentEditState() !== null && milestones().find(m => m.Date === CurrentEditState()!.Date) || null,
        changeInProgress = () => !!focusedMilestone()?.ChangedDate();

    const Loading = S(() =>
        initialDataLoading()
     || CurrentEditPhase() === EditPhase.Committing
     || milestones().some(m => m.BerthAssignment()?.Loading())
    );

    return {
        PortCall,
        Framework,
        CurrentEditState,
        CanMovePortCall,
        MovePortCall,
        Loading,
        Header: headerModel({ PortCall })
    };

    function Milestone(params : MilestoneParams) {
        // related entities
        const
            { Date, MilestoneDates } = params,
            metroMapMilestone = metroMapMilestoneModel(params);

        // movement properties
        const
            HasOccurred = () => Date.Actual() !== null;
            // Testing out a looser UX on logging actuals; commenting out incase we revert based off user feedback
            // index = () => MilestoneDates().indexOf(Date),
            // isFirstWithoutActual = () => { const i = index(); return Date.Actual() === null && !MilestoneDates().some((m, j) => j < i && m.Actual() === null); },
            // isLastWithActual = () => { const i = index(); return Date.Actual() !== null && !MilestoneDates().some((m, j) => j > i && m.Actual() !== null); };

        // pending schedule events for this milestone
        const
            enteredEstimate = eventIndex(buildEventKey(Date.Id(), SDEType.EstimatedDateEntered)),
            enteredActual = eventIndex(buildEventKey(Date.Id(), SDEType.ActualDateEntered)),
            enteredEffective = () => enteredActual() || enteredEstimate(),
            changedEstimate = eventIndex(buildEventKey(Date.Id(), SDEType.EstimatedDateChanged)),
            ChangedActual = eventIndex(buildEventKey(Date.Id(), SDEType.ActualDateChanged));

        // pending schedule events for the end of this milestone segment
        const
            segmentEnd = () => metroMapMilestone.NextDate(),
            segmentEndChangedEstimate = eventIndex(() => buildEventKey(segmentEnd()?.Id() ?? 0, SDEType.EstimatedDateChanged)),
            segmentEndChangedActual = eventIndex(() => buildEventKey(segmentEnd()?.Id() ?? 0, SDEType.ActualDateChanged)),
            segmentEndChangedDate = () => segmentEndChangedActual() ?
                // Similar handling of clearing actual as for ChangedDate declared below. See comment there.
                (segmentEndChangedActual()!.Date() ?? segmentEndChangedEstimate()?.Date() ?? segmentEnd()?.Estimated() ?? null) :
                segmentEndChangedEstimate()?.Date() ?? null;

        // display and edit values that respect the above pending events
        const
            DateEntry = () => enteredEffective() && enteredEffective()!.Date,
            ChangedDate = () => ChangedActual() ?
                // Careful: If clearing actual, make sure to fallback to the saved estimated date (after a
                // defensive check to make sure we don't have a changed estimate too, which would be unexpected).
                // Basically, this says that the date is changing from the actual value to the estimated value.
                (ChangedActual()!.Date() ?? changedEstimate()?.Date() ?? Date.Estimated()) :
                changedEstimate()?.Date() ?? null,
            DisplayDate = () => ChangedDate() ?? Date.Effective(),
            // The DisplayDate is an estimate (might be the committed estimate or might be a new estimate in
            // progress).
            DisplayDateIsEstimated = () => ChangedActual() ? ChangedActual()!.Date() === null : Date.Actual() === null,
            SegmentEndDate = () => segmentEndChangedDate() ?? segmentEnd()?.Effective() ?? null;

        // status of current edit state and pending changes
        const
            DateHasEditFocus = () => CurrentEditState()?.Date === Date ? CurrentEditState() : null,
            DateIsInEditScope = () => CurrentEditState()?.EditScope.includes(Date) || ChangedDate() !== null,
            NextDateIsInEditScope = () =>
                !!segmentEnd() && CurrentEditState()?.EditScope.includes(segmentEnd()!) || segmentEndChangedDate() !== null,
            DurationIsInEditScope = () =>
                DateIsInEditScope()  && !NextDateIsInEditScope() ||
                !DateIsInEditScope() &&  NextDateIsInEditScope() ||
                !!segmentEnd() && (+SegmentEndDate()! - +DisplayDate()) !== (+segmentEnd()!.Effective() - +Date.Effective()),
            enteredEstimateWasAccepted = () =>
                !!enteredEstimate() && !!changedEstimate() && TZDate.nullableEq(enteredEstimate()!.Date(), changedEstimate()!.Date()),
            enteredActualWasAccepted = () =>
                !!enteredActual()   && !!ChangedActual()   && TZDate.nullableEq(enteredActual()!.Date(), ChangedActual()!.Date()),
            entryWasAccepted = () => enteredEstimateWasAccepted() || enteredActualWasAccepted(),
            // The user entered a date that differs from the current value, yet our schedule change engine either
            // didn't accept the users entry at all or only accepted it partially.
            DateEntryWasRejected = () => !!enteredEffective() && !TZDate.nullableEq(enteredEffective()!.Date(), Date.Effective()) && !entryWasAccepted();

        // actions
        const
            // don't allow user to switch focus if they've got pending changes on another milestone
            CanEdit = () => CanEditPortCall() && !changeInProgress(),
            EditDate = () => CanEdit() && SetEditState(
                Date.Actual() !== null                           ? EditActualState(Date, pcChangesCommitHandler) :
                Date.MilestoneTypeId() === MSType.FirstArrival   ? EditFirstArrivalState(Date, pcChangesCommitHandler) :
                Date.MilestoneTypeId() === MSType.FinalDeparture ? EditFinalDepartureState(Date, pcChangesCommitHandler) :
                Date.MilestoneTypeId() === MSType.BerthArrival   ? EditBerthArrivalState(Date, pcChangesCommitHandler) :
                Date.MilestoneTypeId() === MSType.ShiftDeparture ? EditShiftDepartureState(Date, pcChangesCommitHandler) :
                null
            ),
            ReviewChanges = () => {
                // We'll allow moving to the review stage so long as the user's entered valued was accepted as is
                // by our schedule change engine. Figure that's clearer to the user than moving to the review stage
                // w/ a date that differs from what the user entered in.
                if (entryWasAccepted()) {
                    CurrentEditState()?.Phase(EditPhase.Reviewing);
                } else if (enteredEffective() && TZDate.nullableEq(enteredEffective()!.Date(), Date.Effective())) {
                    // The user didn't make any changes, which is why our schedule change engine didn't record a
                    // change event for their entry. Just treat that as equivalent to cancelling the edit.
                    SetEditState(null);
                } else {
                    // Apparently the user DID enter a new value, but our schedule change engine rejected the user's
                    // entry either partly or in full. Stay in editing mode so the user can fix the problem.
                }
            },
            // Testing out a looser UX on logging actuals; commenting out incase we revert based off user feedback
            // CanLogActual = () => CanEditActuals() && isFirstWithoutActual() && !changeInProgress(),
            // CanClearActual = () => CanEditActuals() && isLastWithActual() && !changeInProgress(),
            CanLogActual = () => CanEditActuals() && !HasOccurred() && !changeInProgress(),
            CanClearActual = () => CanEditActuals() && HasOccurred() && !changeInProgress(),
            LogActual = () => CanLogActual() && SetEditState(LogActualState(Date, pcChangesCommitHandler)),
            ClearActual = () => CanClearActual() && SetEditState(ClearActualState(Date, pcChangesCommitHandler));

        // Berth assignment and C-Cards
        const
            BerthAssignment = S(() =>
                Date.IsBerthArrivalDate() &&
                Date.ItineraryPoint()?.BerthReservation() ?
                    BerthAssignmentViewModel(
                        Date.ItineraryPoint()!.BerthReservation()!,
                        model.Rights,
                        ajax,
                        entrypoints,
                        berthAssgnDetailsModel
                    ) :
                    null
            ),
            CurrentCoordinationCards = Date.CoordinationCards
                .map(ccard => CurrentCCardViewModel(ccard, ccardController, ccardDetailsModel)),
            ShowObsoleteCoordinations = S.value(false),
            CancelledCoordinationCards =
                lift(() => {
                    // A representative version for each deleted C-Card we know of. This may or may not be the
                    // most recent version of that C-Card.
                    const representativeVersions = new Map<number, CoordinationCardVersion>();
                    for (let v of Date.Self()?.DeletedCoordinationCardVersions() ?? []) {
                        representativeVersions.set(v.Id(), v);
                    }
                    return Array.from(representativeVersions.values());
                })
                // Grab the C-Card self for each of these versions. Upserting it ourselves covers the case where
                // the server might not have sent us the Self object yet.
                .map(v => data.CoordinationCardSelves.update1(null, { Id: v.Id() }))
                // Make sure that C-Card self has no Live version that we know of. Also ignore C-Card selves
                // that have no known versions, which can happen b/c of a delay (in terms of S's clock)
                // populating the Versions property.
                .filter(self => self.Live() === null && self.Versions().length > 0)
                // Grab the most recent version we know of for each C-Card self
                .map(self => self.Versions().reduce(
                    (mostRecent, next) => next.ChangeId() > mostRecent.ChangeId() ? next : mostRecent
                ))
                // Make sure this version says it's Deleted. This is defensive, as we already checked above that
                // we don't have a Live version of this C-Card. Also make sure this version says it belongs to
                // this milestone date, to guard against the possibility that this C-Card may have been
                // reassigned to a different milestone date since the deletion we started from above.
                .filter(v => v.Deleted() && v.ScheduleDateId() === Date.Id())
                .map(v => CancelledCCardViewModel(v, Date, ShowObsoleteCoordinations, cancelledCCardController, cancelledCCardDetailsModel)),
            // All current cards plus cancelled cards that match our ShowObsoleteCoordinations flag.
            VisibleCoordinationCards = (CurrentCoordinationCards as ArraySignal<CurrentCCardViewModel | CancelledCCardViewModel>)
                .concat(CancelledCoordinationCards.filter(ccard => ccard.IsVisible()))
                .sort((ccard1, ccard2) => CoordinationCard.DisplayOrder(ccard1.Model, ccard2.Model)),
            // Cancelled coordinations that no longer apply to this milestone date (i.e. they were auto-cancelled
            // BECAUSE they no longer applied, say due to a location change).
            ObsoleteCoordinationCards = CancelledCoordinationCards.filter(ccard => !ccard.AppliesToMilestoneDate());

        return {
            ...metroMapMilestone,
            HasOccurred,
            ChangedActual,
            DateEntry,
            ChangedDate,
            DisplayDate,
            DisplayDateIsEstimated,
            DateHasEditFocus,
            DateIsInEditScope,
            DurationIsInEditScope,
            DateEntryWasRejected,
            GlobalEditPhase: CurrentEditPhase,
            CanEdit,
            EditDate,
            ReviewChanges,
            CanLogActual,
            LogActual,
            CanClearActual,
            ClearActual,
            SegmentEndDate,
            BerthAssignment,
            ShowObsoleteCoordinations,
            VisibleCoordinationCards,
            ObsoleteCoordinationCards,
            Localization
        };
    }
}

export enum EditType {
    LogActual          = "logactual",
    EditActual         = "editactual",
    ClearActual        = "clearactual",
    EditFirstArrival   = "firstarrival",
    EditFinalDeparture = "finaldeparture",
    EditBerthArrival   = "bertharrival",
    EditShiftDeparture = "shiftdepature",
    Move               = "move"
}

export enum EditPhase {
    Editing      = "editing",
    Reviewing    = "reviewing",
    Committing   = "committing",
    CommitErrors = "commit-errors"
}

interface CommitHandler {
    Commit() : PromiseLike<void>;
    NewValidationWatcher() : () => Errors;
}

interface EditStateParams {
    Type : EditType;
    Date : DateEntity;
    InitialPhase : EditPhase;
    SchedulePolicy : (d : DateEntity) => Policy | null;
    EditScope : (d : DateEntity) => DateEntity[];
    CommitHandler : CommitHandler;
}

export type EditState = ReturnType<typeof EditState>;
function EditState(
    {
        Type,
        Date,
        InitialPhase,
        SchedulePolicy,
        EditScope,
        CommitHandler
    } : EditStateParams,
    onCancelEdit : () => void
) {
    const
        Phase = S.value(InitialPhase),
        CommitErrors = CommitHandler.NewValidationWatcher();

    return {
        Type,
        Date,
        SchedulePolicy: SchedulePolicy(Date),
        EditScope: EditScope(Date),
        Cancel: onCancelEdit,
        Phase,
        CommitErrors,
        Commit
    };

    async function Commit() {
        if (S.sample(Phase) === EditPhase.Committing) return;
        Phase(EditPhase.Committing);
        try {
            await CommitHandler.Commit();
            onCancelEdit();
        } catch (ex) {
            // Validation errors put us in a distinct CommitErrors phase, from which the only option is to
            // cancel the edit. All other errors (like 500 errors) put us back in the Reviewing phase, in case
            // users decide they wanna retry the commit.
            Phase(Object.keys(CommitErrors()).length > 0 ? EditPhase.CommitErrors : EditPhase.Reviewing);
            throw ex;
        }
    }
}

const
    EditMovementStartPolicy = (d : DateEntity) : Policy => pc => {
        if (!d.IsMovementStartDate()) throw new Error("Cannot use movement start policy for milestone that isn't one");
        const ip = d.ItineraryPoint(),
            ipDeparture = ip && ip.DepartureEntity(),
            prev = ip && ip.PreviousMovementPoint(),
            prevArrival = prev && prev.ArrivalEntity(),
            next = ip && ip.NextMovementPoint(),
            nextArrival = next && next.ArrivalEntity(),
            nextDeparture = next && next.DepartureEntity();
        return commonScheduleRules(pc).concat([
            // arrival at previous IP is anchored
            anchorDate(prevArrival),
            // transit duration is fixed, unless next arrival is an actual
            fixOffset(ipDeparture, nextArrival),
            // departure from next IP is anchored unless it's the port departure
            next && next.IsPortDeparture() ? null : anchorDate(nextDeparture)
        ]);
    },
    EditBerthArrivalPolicy = (d : DateEntity) : Policy => pc => {
        if (!d.IsBerthArrivalDate()) throw new Error("Cannot use berth arrival policy for milestone that isn't one");
        const ip = d.ItineraryPoint(),
            prev = ip && ip.PreviousMovementPoint();
        return commonScheduleRules(pc).concat([
            // departure from this IP is anchored
            anchorDate(ip && ip.DepartureEntity()),
            // departure from previous IP is anchored
            anchorDate(prev && prev.DepartureEntity()),
        ]);
    },
    LogOrEditActualPolicy = (_d : DateEntity) : Policy => pc => {
        // Actuals get committed straight to the schedule with the smallest possible impact on adjacent estimates.
        // See notes on actuals handling in scheduleChangeEngine.ts for more.
        return commonScheduleRules(pc);
    },
    MovePortCallPolicy = (d : DateEntity) : Policy => pc =>
        // all dates move with the one being edited
        pc.Schedule().filter(o => o !== d).map(o =>
            fixOffset(d, o)!
        ),
    anchorDate = (d : DateEntity | null) =>
        d && new Rule(d, Rel.Eq, d.Estimated()),
    fixOffset = (from : DateEntity | null, to : DateEntity | null) =>
        from && to && new Rule(from, Rel.Eq, to, +to.Estimated() - +from.Estimated()),
    movementScope = (d : DateEntity) => {
        const next = d.ItineraryPoint() && d.ItineraryPoint()!.NextMovementPoint() && d.ItineraryPoint()!.NextMovementPoint()!.ArrivalEntity();
        return next && next.Actual() === null ? [d, next] : [d];
    },
    allScope = (d : DateEntity) => d.PortCall() ? d.PortCall()!.Schedule() : [d],
    singleScope = (d : DateEntity) => [d],
    createScheduleEditState = (Type : EditType, policy : (d : DateEntity) => Policy, scope : (d : DateEntity) => DateEntity[], initialPhase : EditPhase) =>
        (Date : DateEntity, CommitHandler : CommitHandler) : EditStateParams => ({
            Type,
            Date,
            InitialPhase: initialPhase,
            SchedulePolicy: policy,
            EditScope: scope,
            CommitHandler
        }),
    EditFirstArrivalState = createScheduleEditState(EditType.EditFirstArrival, EditMovementStartPolicy, movementScope, EditPhase.Editing),
    EditBerthArrivalState = createScheduleEditState(EditType.EditBerthArrival, EditBerthArrivalPolicy, singleScope, EditPhase.Editing),
    EditShiftDepartureState = createScheduleEditState(EditType.EditShiftDeparture, EditMovementStartPolicy, movementScope, EditPhase.Editing),
    EditFinalDepartureState = createScheduleEditState(EditType.EditFinalDeparture, EditMovementStartPolicy, movementScope, EditPhase.Editing),
    LogActualState = createScheduleEditState(EditType.LogActual, LogOrEditActualPolicy, singleScope, EditPhase.Editing),
    EditActualState = createScheduleEditState(EditType.EditActual, LogOrEditActualPolicy, singleScope, EditPhase.Editing),
    MovePortCallState = createScheduleEditState(EditType.Move, MovePortCallPolicy, allScope, EditPhase.Editing),
    ClearActualState = createScheduleEditState(EditType.ClearActual, LogOrEditActualPolicy, singleScope, EditPhase.Reviewing);

export type BerthAssignmentViewModel = ReturnType<typeof BerthAssignmentViewModel>;
function BerthAssignmentViewModel(
    BerthReservation : BerthReservation,
    rights : () => PortCallRights,
    ajax : IAjax,
    entrypoints : Entrypoints,
    detailsModel : (params : BerthAssignmentDetailsParams) => BerthAssignmentDetails,
) {
    const
        Issues = BerthReservation.Issues.sort(Issue.ReversePrecedenceOrder),
        SaveMutex = new Mutex(),
        ShowDetails = S.value(false),
        Details = S(() => ShowDetails() ?
            detailsModel({
                BerthReservation,
                DismissDetails: () => ShowDetails(false),
                SaveMutex
            }) :
            null
        ),
        bookUnbookReq : BookReservationsRequestData & UnbookReservationsRequestData = { ReservationIds: [BerthReservation.Id()] },
        bookClient   = ajax.Url<BookReservationsRequestData  , unknown>(entrypoints.BookReservations  , { useCache: true }),
        unbookClient = ajax.Url<UnbookReservationsRequestData, unknown>(entrypoints.UnbookReservations, { useCache: true }),
        booking   = S.value(false),
        unbooking = S.value(false),
        hasBookPC = () => (rights() & PortCallRights.Book) !== 0,
        CanBook   = () => hasBookPC() && BerthReservation.Bookable(),
        CanUnbook = () => hasBookPC() && BerthReservation.Unbookable(),
        Loading = () => booking() || unbooking();

    return {
        BerthReservation,
        Issues,
        Details,
        CanBook,
        CanUnbook,
        Loading,
        ToggleDetails,
        Book,
        Unbook
    };

    function ToggleDetails() {
        ShowDetails(!S.sample(ShowDetails));
    }

    async function Book() {
        if (booking()) return;
        booking(true);
        try {
            const guard = await SaveMutex.acquire();
            try {
                await bookClient.Post(bookUnbookReq);
            } finally {
                guard.release();
            }
        } finally {
            booking(false);
        }
    }

    async function Unbook() {
        if (unbooking()) return;
        unbooking(true);
        try {
            const guard = await SaveMutex.acquire();
            try {
                await unbookClient.Post(bookUnbookReq);
            } finally {
                guard.release();
            }
        } finally {
            unbooking(false);
        }
    }
}

export enum CCardCurrency {
    Current = 'current',
    Cancelled = 'cancelled'
}

const DEFAULT_CCARD_CONTROLLER : CoordinationCardController = {};

export type CurrentCCardViewModel = ReturnType<typeof CurrentCCardViewModel>;
function CurrentCCardViewModel(
    ccard : CoordinationCard,
    controllerFactory : ((_ccard : CoordinationCard) => CoordinationCardController) | undefined,
    detailsModel : (params : CoordinationCardDetailsParams) => CoordinationCardDetails
) {
    const
        controller = controllerFactory?.(ccard) ?? DEFAULT_CCARD_CONTROLLER,
        ShowDetails = S.value(false),
        Details = S(() => ShowDetails() ?
            detailsModel({
                CoordinationCard: ccard,
                DismissDetails: () => ShowDetails(false),
                ShouldPauseLogUpdates: controller.ShouldPauseLogUpdates
            }) :
            null
        );

    return {
        Currency: CCardCurrency.Current as const,
        Model: ccard,
        Details,
        ToggleDetails
    };

    function ToggleDetails() {
        ShowDetails(!S.sample(ShowDetails));
    }
}

const DEFAULT_CANCELLED_CCARD_CONTROLLER : CancelledCoordinationCardController = {};

export type CancelledCCardViewModel = ReturnType<typeof CancelledCCardViewModel>;
function CancelledCCardViewModel(
    ccv : CoordinationCardVersion,
    Date : DateEntity,
    ShowObsoleteCoordinations : () => boolean,
    controllerFactory : ((_ccv : CoordinationCardVersion) => CancelledCoordinationCardController) | undefined,
    detailsModel : (params : CancelledCoordinationCardDetailsParams) => CancelledCoordinationCardDetails
) {
    const
        AppliesToMilestoneDate = S(() => ccv.Coordination()?.AppliesToScheduleDate(Date) ?? false),
        IsVisible = () => AppliesToMilestoneDate() || ShowObsoleteCoordinations(),
        controller = controllerFactory?.(ccv) ?? DEFAULT_CANCELLED_CCARD_CONTROLLER,
        ShowDetails = S.value(false),
        Details = S(() => ShowDetails() ?
            detailsModel({
                CoordinationCard: ccv,
                DismissDetails: () => ShowDetails(false),
                ShouldPauseLogUpdates: controller.ShouldPauseLogUpdates
            }) :
            null
        );

    // Hide details if the C-Card itself is hidden
    S(() => IsVisible() || ShowDetails(false));

    return {
        Currency: CCardCurrency.Cancelled as const,
        Model: ccv,
        AppliesToMilestoneDate,
        IsVisible,
        Details,
        ToggleDetails
    };

    function ToggleDetails() {
        ShowDetails(!S.sample(ShowDetails));
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/portcallschedule/model.ts